import { useState, useMemo, useRef, useEffect, useCallback } from 'react';

import config from '../../config/config';
import authHeader from '../../config/authHeader';

import {
    ScheduleComponent,
    ViewsDirective, ViewDirective,
    ResourcesDirective, ResourceDirective,
    Day, Week, WorkWeek, Month, Agenda,
    TimelineViews, TimelineMonth,
    Resize, DragAndDrop, Inject
} from '@syncfusion/ej2-react-schedule';

import useUsers from "../../hooks/useUsers";

const now = new Date();
const todayAt = (h, m = 0) => new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0);

const ResourceHeader = (props) => {
    const name = props.resourceData?.Name ?? '';
    const role = props.resourceData?.Role ?? '';
    const color = props.resourceData?.Color ?? '#999';
    const initials = useMemo(() => name.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase(), [name]);

    return (
        <div className="flex items-center gap-2 py-1">
            <div
                title={name}
                style={{ background: color }}
                className="flex justify-center items-center rounded-full w-8 h-8 font-semibold text-white text-sm"
            >
                {initials}
            </div>
            <div className="leading-tight">
                <div className="font-medium">{name}</div>
                <div className="opacity-70 text-[11px]">{role}</div>
            </div>
        </div>
    );
};

const AgendaApp = () => {
    const [loadingRDV, setLoadingRDV] = useState(true);
    const [lstRdv, setLstRdv] = useState([]);

    const { users, fetchUsers } = useUsers();

    const scheduleRef = useRef(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        setLoadingRDV(true);

        fetch(`${config.apiBaseUrl}/4DACTION/KST_getExpertises`, {
            method: 'GET',
            headers: authHeader()
        })
            .then((response) => response.json())
            .then((data) => {
                setLstRdv(data);
            })
            .catch((error) => {
                console.error("Erreur lors de la récupération des rendez-vous:", error);
                setLoadingRDV(false);
            });

        setLoadingRDV(false);
    }, []);

    // --- helpers date/heure ---
    const isZeroDate = (s) => !s || s === "0000-00-00";
    const pad2 = (n) => String(n).padStart(2, "0");
    const secToHM = (sec = 0) => ({ h: Math.floor(sec / 3600), m: Math.floor((sec % 3600) / 60) });
    const makeDateTime = (ymd, h, m) =>
        new Date(`${ymd}T${pad2(h)}:${pad2(m)}:00`);

    const USERS = useMemo(() => {
        return (users ?? [])
            .slice(0, 5)
            .map((u, idx) => ({
                Name: `${u?.Nom ?? ""} ${u?.Prenom ?? ""}`.trim() || "Sans nom",
                UserUUID: idx + 1,
                Color: u?.color ?? "#FF5733",
                Role: u?.role ?? "",
            }));
    }, [users]);

    const findUserIdByCA = useCallback((ca) => {
        if (!USERS.length) return undefined;
        if (!ca) return USERS[0].UserUUID;

        const firstBloc = ca.split("-")[0].trim().toLowerCase();
        const firstNameToken = firstBloc.split(/\s+/)[0];

        const match =
            USERS.find(u => u.Name.toLowerCase().includes(firstBloc)) ||
            USERS.find(u => u.Name.toLowerCase().includes(firstNameToken));

        return match?.UserUUID ?? USERS[0].UserUUID;
    }, [USERS]);

    const EVENTS = useMemo(() => {
        if (!Array.isArray(lstRdv)) return [];

        return lstRdv.map((rdv, idx) => {
            const ymd =
                !isZeroDate(rdv?.Date_Convoc) ? rdv.Date_Convoc :
                    !isZeroDate(rdv?.Date_Rdv) ? rdv.Date_Rdv :
                        null;

            console.log("Rdv date:", rdv?.Date_Convoc, rdv?.Date_Rdv, "=> ymd =", ymd);

            const { h, m } = secToHM(rdv?.Heure_Convoc || rdv?.Heure_Rdv || 3600 * 9);

            const start = ymd ? makeDateTime(ymd, h, m) : todayAt(9, 0);
            const end = new Date(start.getTime() + 60 * 60 * 1000);

            return {
                Id: rdv?.UUID_ || `tmp-${idx}`,
                Subject: rdv?.Titre || rdv?.Type_Document || "Rendez-vous",
                StartTime: start,
                EndTime: end,
                Description: rdv?.Resume || "",
                Location: rdv?.Four_Nom || "",
                UserUUID: findUserIdByCA(rdv?.CA),
            };
        });
    }, [lstRdv, findUserIdByCA]);

    return (
        <div className="p-2">
            <ScheduleComponent
                ref={scheduleRef}
                locale="fr"
                width="100%"
                height="700px"
                // Vue par défaut = Semaine de travail
                currentView="Day"
                selectedDate={new Date()}
                // Plage horaire visible (adaptable)
                startHour="08:00"
                endHour="19:00"
                // Jours de travail (lun->ven)
                workDays={[1, 2, 3, 4, 5]}
                // Grille horaire
                timeScale={{ interval: 60, slotCount: 2 }}
                // Groupement vertical par utilisateur
                group={{ resources: ['Users'], orientation: 'Vertical' }}
                // Données locales
                eventSettings={{
                    dataSource: EVENTS,
                    fields: {
                        id: 'Id',
                        subject: { name: 'Subject', title: 'Objet' },
                        startTime: { name: 'StartTime', title: 'Début' },
                        endTime: { name: 'EndTime', title: 'Fin' },
                        description: { name: 'Description', title: 'Description' },
                        location: { name: 'Location', title: 'Lieu' }
                    }
                }}
                resourceHeaderTemplate={ResourceHeader}
            >
                {/* Ressources (utilisateurs) */}
                <ResourcesDirective>
                    <ResourceDirective
                        field="UserUUID"
                        title="Utilisateurs"
                        name="Users"
                        allowMultiple={false}
                        dataSource={USERS}
                        textField="Name"
                        idField="UserUUID"
                        colorField="Color"
                    />
                </ResourcesDirective>

                {/* Vues disponibles (verticales + timeline si besoin) */}
                <ViewsDirective>
                    <ViewDirective option="Day" />
                    <ViewDirective option="WorkWeek" /> {/* par défaut */}
                    <ViewDirective option="Week" />
                    <ViewDirective option="Month" />
                    <ViewDirective option="Agenda" />
                    {/* Optionnel : si tu veux une version timeline plus tard
          <ViewDirective option="TimelineDay" />
          <ViewDirective option="TimelineMonth" />
          */}
                </ViewsDirective>

                <Inject services={[
                    Day, Week, WorkWeek, Month, Agenda,
                    TimelineViews, TimelineMonth, // (laisse-les injectés si tu actives les timelines plus tard)
                    Resize, DragAndDrop
                ]} />
            </ScheduleComponent>

            {/* Un peu de styles tailwind-friendly (optionnel) */}
            <style>{`
        .e-schedule .e-vertical-view .e-resource-left-td {
          width: 220px; /* un peu plus large pour le header */
        }
      `}</style>
        </div>
    );
};

export default AgendaApp;
